import React, { Fragment, useEffect, useState } from 'react';
import styles from '../styles.module.scss';
import cN from 'classnames';

import { useSelector, useDispatch } from 'react-redux';
import { setMiniRuleInfo, setFareRuleInfo } from '@Redux/search/actions';
import { I_FareInfos, I_FlightInfos, I_ItineraryInfos } from '@Redux/search/interfaces';
import { RootState } from '@Redux/search/store';
import { getProductUrl } from '../utils';
import { isForTesting } from '@utils/domainByEnv';
import { isTFGDS, isSuitPlan, getIconImageURL } from '@utils/utils';
import { formatPrice, displayTaxRtow } from '@utils/formatDisplay';


import FlightSegment from './FlightSegment';
import PriceDetail from './PriceDetail';

import * as Tag from '@features/ui/Tag';
import { Dialog } from '@lion-design/core';
import { FlightDetailCard } from '@features/flightInfo';
import { TicketRuleLCC, TicketRule, FareRuleDetail } from '@features/ticketRules';
import { AirPlaneIcon, FilesIcon, ArrowUpIcon, NoticeIcon, InfoIcon } from '@features/ui/Icons';
import { I_ItineraryInfo } from '@interfaces/common';
import useShowControl from '@hooks/useShowControl';

import BubbleBox from '@features/ui/BubbleBox';


interface I_FlightCardProps {
    flightData: I_FlightInfos;
    lowestPriceData: I_FlightInfos | undefined;
    lowestTimeData: I_FlightInfos | undefined;
}


const FlightCard = ({ flightData, lowestPriceData, lowestTimeData }: I_FlightCardProps) => {
    const moduleName = 'flightCard';


    const { UrlParams, ShowData, MiniRuleInfo, FareRuleInfo } = useSelector(
        (state: RootState) => state.search
    );
    const dispatch = useDispatch();

    const {
        close: closeFlightInfo,
        toggle: toggleFlightInfo,
        isOpen: isFlightInfoOpen,
    } = useShowControl();

    const {
        open: openTicketRule,
        close: closeTicketRule,
        isOpen: isTicketRuleOpen,
    } = useShowControl();

    const {
        open: openFareRuleInfo,
        close: closeFareRuleInfo,
        isOpen: isFareRuleInfoOpen,
    } = useShowControl();


    const { FareInfos, ItineraryInfos, ValidatingAirline, ValidatingAirlineName, lowTime, IsLcc, SeqNo } =
        flightData;
    const transactionKey = ShowData.data.TransactionKey;
    const searchKey = ShowData.data.SearchKey;
    const fareFamilyKey = FareInfos[0].FlightFareInfoKey;
    const productUrl = getProductUrl({ transactionKey, searchKey, fareFamilyKey });
    const airlineLogoUrl = getIconImageURL(ValidatingAirline);

    // 顯示資料更新時關閉所有打開的區塊
    useEffect(() => {
        closeTicketRule();
        closeFlightInfo();
    }, [ShowData, closeTicketRule, closeFlightInfo]);

    // 開啟票價規定光箱
    const handleOpenTicketRule = () => {
        const targetFareData = FareInfos.filter(
            (item: I_FareInfos) => item.FlightFareInfoKey === fareFamilyKey
        );
        const cheapestFareData = [FareInfos[0]];

        const reqData = {
            ...ShowData.data,
            FlightInfo: {
                SeqNo: flightData.SeqNo,
                ValidatingAirline: ValidatingAirline,
                ValidatingAirlineName: ValidatingAirlineName,
                ItineraryInfos: flightData.ItineraryInfos,
                FareInfos: isSuit ? cheapestFareData : targetFareData,
            },
        };
        delete reqData.FlightInfos;

        dispatch(setMiniRuleInfo(reqData));
        openTicketRule();
    };

    //業務版本的開啟詳細票規光箱
    const handleOpenFareRuleInfo = () => {
        const targetFareData = FareInfos.filter(
            (item: I_FareInfos) => item.FlightFareInfoKey === fareFamilyKey
        );
        const cheapestFareData = [FareInfos[0]];

        const reqData = {
            ...ShowData.data,
            FlightInfo: {
                SeqNo: flightData.SeqNo,
                ValidatingAirline: ValidatingAirline,
                ValidatingAirlineName: ValidatingAirlineName,
                ItineraryInfos: flightData.ItineraryInfos,
                FareInfos: isSuit ? cheapestFareData : targetFareData,
            },
        };
        delete reqData.FlightInfos;

        dispatch(setFareRuleInfo(reqData));
        openFareRuleInfo();
    };


    const isSuit = isSuitPlan(FareInfos[0].FareFamilyName);
    const isTF = isTFGDS(FareInfos[0].TicketSupplier);

    const isLowestTime = lowestTimeData?.lowTime === lowTime;
    const isLowestPrice = FareInfos[0].AdtPrice === lowestPriceData?.FareInfos[0].AdtPrice;
    const hasGift = !!FareInfos[0].GiftNo;

    const ticketsLeft = flightData.FareInfos[0].AdtAvailSeats;


    return (
        <div className={cN(moduleName, styles[moduleName])} >
            <div className={styles.wrapper}>
                <div className={styles.tag}>
                    {hasGift && <Tag.Featured text="嚴選禮品" isHalfRadius={true} />}
                    {isLowestTime && <Tag.Featured text="最省時" isHalfRadius={true} />}
                    {isLowestPrice && <Tag.Featured text="最低價" isHalfRadius={true} />}
                </div>

                <div className={styles.infoBox}>
                    <div className={styles.flightInfo}>
                        {ItineraryInfos?.length > 0 &&
                            ItineraryInfos.map((itinerary) => (
                                <FlightSegment key={itinerary.SeqNo} data={itinerary} />
                            ))}
                    </div>
                    <div className={styles.logo}>
                        <object type="image/png" data={getIconImageURL(ValidatingAirline)}>
                            <img src={getIconImageURL()} loading="lazy" alt="default logo" />
                        </object>
                        <div>{ValidatingAirlineName}</div>
                        {IsLcc && <div className={styles.lcc}>廉價航空</div>}
                        {window.salesmode && <div className={styles.supplier}>{FareInfos[0].TicketSupplier}</div>}
                        {/* {isForTesting && (
                            <span style={{ fontWeight: 'normal' }}>
                                🎟️{FareInfos[0].TicketSupplier}
                            </span>
                        )} */}
                    </div>

                    {/* 業務版本的票價，電腦版用BubbleBox，手機版用Dialog */}
                    {window.salesmode &&
                        <div className={styles.price}>
                            <div>含稅參考總價 TWD {formatPrice(FareInfos[0].TotalPrice)} </div>
                            <div className={styles.salesOrder}>
                                人工訂位 <span> TWD {formatPrice(FareInfos[0].AdtPrice)}</span>
                                <BubbleBox content={<PriceDetail flightData={flightData} isB2C={false} />}><InfoIcon fill="#f03742" /></BubbleBox>
                            </div>
                            {FareInfos[0].SalesInfos && <div className={styles.bcOrder}>
                                B2C訂位 <span>TWD {formatPrice(FareInfos[0].SalesInfos?.LION ? FareInfos[0].SalesInfos.LION.AdtAmt : 0)}</span>
                                {FareInfos[0].SalesInfos.LION?.Formula && <BubbleBox
                                    content={<PriceDetail flightData={flightData} isB2C={true} />}
                                >
                                    <InfoIcon
                                        fill="#222222" />
                                </BubbleBox>}
                            </div>}
                            <div>大人含稅參考價</div>
                        </div>
                    }


                    {/* 一般旅客版本的票價 */}
                    {!window.salesmode &&
                        <div className={cN(styles.price, styles.bcPrice)}>
                            <div>
                                TWD<span>{formatPrice(FareInfos[0].AdtPrice)}</span>
                            </div>
                            <div>大人{displayTaxRtow(UrlParams.Rtow)}含稅價</div>
                        </div>
                    }

                    <div className={styles.information}>
                        <div onClick={toggleFlightInfo}>
                            <AirPlaneIcon fill="#222" width="16" height="16" />
                            航班資訊
                        </div>
                        <div onClick={handleOpenTicketRule}>
                            <FilesIcon />
                            票價規定
                        </div>
                        {/* 新增業務版本 */}
                        {window.salesmode &&
                            <div onClick={handleOpenFareRuleInfo}>
                                <FilesIcon />
                                詳細票規
                            </div>
                        }
                    </div>
                    <div className={styles.orderBtn}>
                        <a href={productUrl} target="_blank" rel="noreferrer noopener">
                            立即訂位
                        </a>
                        {ticketsLeft <= 4 && !window.salesmode && (
                            <div className={styles.availSeats}>{`僅剩${ticketsLeft}張！`}</div>
                        )}
                        {/* 業務版本要顯示機位數 */}
                        {window.salesmode && <div className={styles.seatsLeft}>{`機位數: ${ticketsLeft} `}</div>}
                    </div>
                </div>
            </div>

            {/* 航班資訊 */}
            <div className={cN(styles.dropdown, { [styles.hide]: !isFlightInfoOpen })}>
                <div className={styles.header}>
                    <div className={styles.changeContent}>
                        {/* {ItineraryInfos.map(
                            (itinerary, i) =>
                                i > 0 &&
                                ItineraryInfos[i].DepAirport !==
                                ItineraryInfos[i - 1].ArrAirport && (
                                    <div key={itinerary.SeqNo} className={styles.changeAirport}>
                                        <NoticeIcon />
                                        {`您將抵達${ItineraryInfos[i - 1].ArrAirportName} ${ItineraryInfos[i - 1].ArrAirport
                                            }，但${UrlParams.Rtow == '3' ? '出發' : '回程'}從${ItineraryInfos[i].DepAirportName
                                            } ${ItineraryInfos[i].DepAirport}`}
                                    </div>
                                )
                        )} */}
                    </div>
                    <div className={styles.pcArrow} onClick={closeFlightInfo}>
                        {window.salesmode && <div className={styles.pcc}>PCC: {FareInfos[0].PccCode}</div>}
                        {!window.salesmode && <div style={{ visibility: 'hidden' }}>PCC: {FareInfos[0].PccCode}</div>}
                        <ArrowUpIcon className={styles.arrowUpIcon} />
                    </div>
                </div>
                <div className={styles.content}>

                    {ItineraryInfos &&
                        ItineraryInfos.map((item: I_ItineraryInfos, i) => (
                            <Fragment key={item.SeqNo}>
                                <FlightDetailCard
                                    fareInfos={FareInfos}
                                    itineraryInfo={item as I_ItineraryInfo}
                                    isTF={isTF}
                                    seqNo={SeqNo}
                                />
                                <div className={styles.changeContent}>
                                    {
                                        i !== ItineraryInfos.length - 1 &&
                                        ItineraryInfos[i + 1].DepAirport !==
                                        ItineraryInfos[i].ArrAirport &&
                                        ItineraryInfos[i + 1].DepCity ===
                                        ItineraryInfos[i].ArrCity && (
                                            <div className={styles.changeAirport}>
                                                <NoticeIcon />
                                                {`您將抵達${ItineraryInfos[i].ArrAirport}${ItineraryInfos[i].ArrAirportName} 
                                                    ，下個航班於${ItineraryInfos[i + 1].DepAirport}${ItineraryInfos[i + 1].DepAirportName}出發`}
                                            </div>
                                        )
                                    }
                                </div>
                            </Fragment>


                        ))}
                </div>
                <div className={styles.mArrow} onClick={closeFlightInfo}>
                    航班資訊
                    <ArrowUpIcon />
                </div>
            </div>

            {/* 套裝方案 */}
            {/* <div className={cN(styles.dropdown, { [`${styles.hide}`]: !showFareInfo })}>
                <div className={styles.header}>
                    <div className={styles.pcArrow} onClick={handleHideAllInfo}>
                        <Icons.ArrowUp />
                    </div>
                </div>
                <div className={styles.content}>
                    <SuitPlan
                        flightData={flightData}
                        fare={FareInfos}
                        handleCallback={(infoKey: string) => handleSuitPlan(infoKey)}
                        isTF={isTF}
                    />
                </div>
                <div className={styles.mArrow} onClick={handleHideAllInfo}>
                    套裝方案
                    <Icons.ArrowUp />
                </div>
            </div> */}

            {/* 票價規定的光箱 */}
            <Dialog
                open={isTicketRuleOpen}
                title="票價規定"
                onClose={closeTicketRule}
                showCloseButton={true}
                className={styles.lightbox}
            >
                <div className={styles.ruleBox}>
                    {
                        (isTF ? (
                            <TicketRuleLCC Rtow={UrlParams.Rtow} miniRule={{ ...MiniRuleInfo }} />
                        ) : (
                            <TicketRule
                                miniRule={{ ...MiniRuleInfo }}
                            />
                        ))}
                </div>
            </Dialog>

            {/* 詳細票規的光箱 */}
            <Dialog
                open={isFareRuleInfoOpen}
                title="詳細票規"
                onClose={closeFareRuleInfo}
                showCloseButton={true}
                className={styles.lightbox}
            >
                <div className={styles.ruleBox}>
                    <FareRuleDetail
                        isLoading={FareRuleInfo.isLoading}
                        Rtow={UrlParams.Rtow}
                        {...FareRuleInfo.data}
                    />
                </div>
            </Dialog>
        </div>
    );
};

export default FlightCard;
